<script>
    // Import necessary Svelte functions and components
    import { getContext, onMount } from 'svelte';
    import { InsightWise } from '.';
    import { stripGID } from '../functions/stripGID';

    // Declare a reactive variable for the user prop
    export let user;

    // Get the context of the Svelte component
    const context = getContext('ctx');

    // Declare variables for conditional script loads, Klaviyo readiness, and user set status
    let mounted = false; // Delay script insertion until CSR.
    let klavioReady = false; // Set to true after Klaviyo is initialized
    let klaviyoUserSet = false; // As soon as user is set in Klaviyo, we set this to true to avoid setting user multiple times
    let mainServiceWorkerRegistered = false; // Set to true when main service worker is registered
    let oneSignalReady = false; // Set to true when OneSignal subscription is ready.
    let oneSignalUserSet = false; // As soon as user is set in OneSignal, we set this to true to avoid setting user multiple times

    // If all services are initalized, set InsightWise writable to true
    $: if (klavioReady /*&& oneSignalReady*/) InsightWise.set(true); // TODO : Hook if adding other event driven tools

    // Subscribe to mainServiceWorkerRegistered
    context.fussyDependanciesReady.mainServiceWorkerRegistered.subscribe((ready) => {
        if (ready !== false) mainServiceWorkerRegistered = ready;
    });

    // If user is logged into Shopify and Klaviyo is ready, set the user
    $: if (!klaviyoUserSet && user?.isUserLoggedInToShopify && klavioReady) {
        klaviyoIdentify(user);
        klaviyoUserSet = true;
    }

    // If main service worker is registered and OneSignal not init, initialize OneSignal (and load its script)
    $: if (mainServiceWorkerRegistered && !oneSignalReady) {
        initalizeOneSignal();
    }

    // If OneSignal is loaded and the user is identified, identify the user to OneSignal
    function pushSubscriptionChangeListener(event) {
        if (event.current.token) {
            oneSignalIdentify();
        }
    }
    $: if (!oneSignalUserSet && oneSignalReady && user?.id) {
        window.OneSignalDeferred.push(async function (OneSignal) {
            let permission = await OneSignal.Notifications.permission; // boolean
            if (permission) {
                oneSignalIdentify();
            }
        });
        window.OneSignalDeferred.push(function (OneSignal) {
            OneSignal.User.PushSubscription.addEventListener('change', pushSubscriptionChangeListener);
        });
    }

    // Function to initialize Klaviyo, if not already initialized and set klavioReady to true.
    function initializeKlavio() {
        // @ts-ignore
        !(function () {
            if (!window.klaviyo) {
                window._klOnsite = window._klOnsite || [];
                try {
                    window.klaviyo = new Proxy(
                        {},
                        {
                            get: function (n, i) {
                                return 'push' === i
                                    ? function () {
                                          var n;
                                          (n = window._klOnsite).push.apply(n, arguments);
                                      }
                                    : function () {
                                          for (var n = arguments.length, o = new Array(n), w = 0; w < n; w++) o[w] = arguments[w];
                                          var t = 'function' == typeof o[o.length - 1] ? o.pop() : void 0,
                                              e = new Promise(function (n) {
                                                  window._klOnsite.push(
                                                      [i].concat(o, [
                                                          function (i) {
                                                              t && t(i), n(i);
                                                          }
                                                      ])
                                                  );
                                              });
                                          return e;
                                      };
                            }
                        }
                    );
                } catch (n) {
                    (window.klaviyo = window.klaviyo || []),
                        (window.klaviyo.push = function () {
                            var n;
                            (n = window._klOnsite).push.apply(n, arguments);
                        });
                }
            }
        })();

        klavioReady = true;
    }

    // Function to set the user in Klaviyo
    function klaviyoIdentify(user) {
        window.klaviyo.push([
            'identify',
            {
                email: user?.email,
                first_name: user?.firstName,
                last_name: user?.lastName
            }
        ]);
    }

    function initalizeOneSignal() {
        window.OneSignalDeferred.push(function (OneSignal) {
            OneSignal.init({
                appId: 'fa6cddd7-abfa-4c68-a8fc-672bd2028ca3'
            }).then(() => {
                oneSignalReady = true;
                context.fussyDependanciesReady.oneSignalAPIReady.set(true);
            });
        });
    }

    // Function to identify the user to OneSignal
    const oneSignalIdentify = () => {
        if (!window?.OneSignal || !user?.id || !user?.email) {
            console.error('OneSignal or UD not available');
            return;
        }
        try {
            window.OneSignal.login(`${user.email}`);
            window.OneSignal.User.addAlias('SID', `${user?.id ? stripGID(user.id) : ''}`);
            oneSignalUserSet = true;
        } catch (e) {
            console.error(e);
        }
    };

    onMount(async () => {
        mounted = true;
    });
</script>

{#if mounted}
    <script async type="text/javascript" src="https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=YuQu8G" on:load={initializeKlavio}></script>
{/if}
{#if mounted && mainServiceWorkerRegistered}
    <script async src="https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js"></script>
{/if}
